import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Eye, EyeOff, GraduationCap, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";

const LOGO = "/assets/brand/logo-traiteur-fl-blanc.webp";

export default function AuthModal() {
  const { authOpen, closeAuth, login, register, social, forgot } = useAuth();
  const nav = useNavigate();

  const [mode, setMode] = useState("login"); // login | register | forgot
  const [accountType, setAccountType] = useState("particulier"); // particulier | corporatif
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({ name: "", email: "", password: "", phone: "", company: "" });

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const reset = () => { setError(""); setBusy(false); };

  const submit = async (e) => {
    e.preventDefault();
    reset();
    setBusy(true);
    try {
      if (mode === "login") {
        await login(form.email, form.password);
      } else if (mode === "register") {
        await register({ ...form, account_type: accountType });
      } else {
        await forgot(form.email);
        setMode("login");
      }
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const handleSocial = async (provider) => {
    reset();
    setBusy(true);
    try { await social(provider, accountType); }
    catch (err) { setError(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const handleScolaire = () => {
    closeAuth();
    window.open("https://ecodemo.dev/parent-login", "_blank", "noopener,noreferrer");
  };

  return (
    <Dialog open={authOpen} onOpenChange={(o) => !o && closeAuth()}>
      <DialogContent
        data-testid="auth-modal"
        className="max-w-md p-0 border border-border bg-card rounded-2xl overflow-hidden gap-0"
      >
        <div className="px-7 pt-8 pb-7 max-h-[88vh] overflow-y-auto">
          <DialogTitle className="sr-only">Connexion à Traiteur FL</DialogTitle>
          <img src={LOGO} alt="Traiteur FL" className="logo-adaptive h-11 w-auto mb-5" />
          <h2 className="font-heading text-2xl font-bold text-foreground">
            Bienvenue chez <span className="text-coral">Traiteur FL</span>
          </h2>
          <p className="text-muted-foreground text-sm mt-1 mb-5">
            {mode === "forgot" ? "Réinitialisez votre mot de passe" : "Connectez-vous pour commander"}
          </p>

          {mode !== "forgot" && (
            <div className="grid grid-cols-2 gap-2 mb-5 p-1 rounded-xl bg-foreground/5">
              <button
                type="button"
                data-testid="auth-tab-particulier"
                onClick={() => setAccountType("particulier")}
                className={`py-2.5 rounded-lg font-heading text-sm font-medium transition-colors ${
                  accountType === "particulier" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-primary-foreground"
                }`}
              >
                👤 Particuliers
              </button>
              <button
                type="button"
                data-testid="auth-tab-corporatif"
                onClick={() => setAccountType("corporatif")}
                className={`py-2.5 rounded-lg font-heading text-sm font-medium transition-colors ${
                  accountType === "corporatif" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-primary-foreground"
                }`}
              >
                🏢 Corporatif
              </button>
            </div>
          )}

          {mode !== "forgot" && (
            <>
              <button
                type="button"
                data-testid="auth-google-button"
                onClick={() => handleSocial("google")}
                className="w-full flex items-center justify-center gap-3 py-3 rounded-lg bg-white text-slate-800 font-heading font-medium hover:bg-slate-100 transition-colors mb-3"
              >
                <img alt="" src="https://www.svgrepo.com/show/475656/google-color.svg" className="h-5 w-5" />
                Continuer avec Google
              </button>
              <button
                type="button"
                data-testid="auth-facebook-button"
                onClick={() => handleSocial("facebook")}
                className="w-full flex items-center justify-center gap-3 py-3 rounded-lg bg-[#1877F2] text-foreground font-heading font-medium hover:bg-[#166fe0] transition-colors mb-5"
              >
                <svg viewBox="0 0 24 24" className="h-5 w-5 fill-white"><path d="M24 12.07C24 5.4 18.63 0 12 0S0 5.4 0 12.07C0 18.1 4.39 23.1 10.13 24v-8.44H7.08v-3.49h3.05V9.41c0-3.02 1.79-4.69 4.53-4.69 1.31 0 2.68.24 2.68.24v2.97h-1.5c-1.49 0-1.96.93-1.96 1.89v2.25h3.33l-.53 3.49h-2.8V24C19.61 23.1 24 18.1 24 12.07z"/></svg>
                Continuer avec Facebook
              </button>

              <div className="flex items-center gap-3 mb-5">
                <div className="h-px flex-1 bg-foreground/10" />
                <span className="text-xs text-muted-foreground uppercase tracking-wider">ou</span>
                <div className="h-px flex-1 bg-foreground/10" />
              </div>
            </>
          )}

          <form onSubmit={submit} className="space-y-3">
            {mode === "register" && (
              <>
                <input
                  data-testid="auth-name-input"
                  required value={form.name} onChange={set("name")}
                  placeholder="Nom complet"
                  className="w-full px-4 py-3 rounded-lg bg-foreground/5 border border-border text-foreground placeholder:text-muted-foreground focus:border-coral focus:outline-none transition-colors"
                />
                {accountType === "corporatif" && (
                  <input
                    data-testid="auth-company-input"
                    value={form.company} onChange={set("company")}
                    placeholder="Entreprise"
                    className="w-full px-4 py-3 rounded-lg bg-foreground/5 border border-border text-foreground placeholder:text-muted-foreground focus:border-coral focus:outline-none transition-colors"
                  />
                )}
                <input
                  data-testid="auth-phone-input"
                  value={form.phone} onChange={set("phone")}
                  placeholder="Téléphone"
                  className="w-full px-4 py-3 rounded-lg bg-foreground/5 border border-border text-foreground placeholder:text-muted-foreground focus:border-coral focus:outline-none transition-colors"
                />
              </>
            )}

            <input
              data-testid="auth-email-input"
              required type="email" value={form.email} onChange={set("email")}
              placeholder="Adresse courriel"
              className="w-full px-4 py-3 rounded-lg bg-foreground/5 border border-border text-foreground placeholder:text-muted-foreground focus:border-coral focus:outline-none transition-colors"
            />

            {mode !== "forgot" && (
              <div className="relative">
                <input
                  data-testid="auth-password-input"
                  required type={showPw ? "text" : "password"} value={form.password} onChange={set("password")}
                  placeholder="Mot de passe"
                  className="w-full px-4 py-3 pr-12 rounded-lg bg-foreground/5 border border-border text-foreground placeholder:text-muted-foreground focus:border-coral focus:outline-none transition-colors"
                />
                <button
                  type="button"
                  data-testid="auth-toggle-password"
                  onClick={() => setShowPw((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                >
                  {showPw ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            )}

            {mode === "login" && (
              <div className="text-right">
                <button
                  type="button"
                  data-testid="auth-forgot-link"
                  onClick={() => { setMode("forgot"); setError(""); }}
                  className="text-sm text-coral hover:text-coral-hover transition-colors"
                >
                  Mot de passe oublié ?
                </button>
              </div>
            )}

            {error && <p data-testid="auth-error" className="text-sm text-red-400">{error}</p>}

            <button
              type="submit"
              disabled={busy}
              data-testid="auth-submit-button"
              className="w-full py-3 rounded-lg bg-primary hover:opacity-90 text-primary-foreground font-heading font-semibold transition-colors active:scale-[0.98] disabled:opacity-60 flex items-center justify-center gap-2"
            >
              {busy && <Loader2 className="h-4 w-4 animate-spin" />}
              {mode === "login" ? "Se connecter" : mode === "register" ? "Créer mon compte" : "Envoyer le lien"}
            </button>
          </form>

          <p className="text-center text-sm text-muted-foreground mt-4">
            {mode === "login" ? (
              <>Pas encore de compte ?{" "}
                <button data-testid="auth-switch-register" onClick={() => { setMode("register"); setError(""); }} className="text-coral hover:underline">Créer un compte</button>
              </>
            ) : (
              <>Déjà un compte ?{" "}
                <button data-testid="auth-switch-login" onClick={() => { setMode("login"); setError(""); }} className="text-coral hover:underline">Se connecter</button>
              </>
            )}
          </p>

          <div className="mt-6 pt-5 border-t border-border">
            <p className="text-xs text-muted-foreground uppercase tracking-widest mb-3">Accès rapide</p>
            <button
              type="button"
              data-testid="auth-scolaire-button"
              onClick={handleScolaire}
              className="w-full flex items-center justify-center gap-2 py-3 rounded-lg bg-transparent border border-border hover:bg-foreground/5 text-foreground font-heading font-semibold transition-colors active:scale-[0.98]"
            >
              <GraduationCap className="h-5 w-5" /> Commande Scolaire
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
